import { ColumnConfig, Button, Tooltip } from "@skyportal/ui-kit";
import { Bucket } from "containers/BucketsPageContent/mappers/bucketsMapper";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatStorageSize } from "utils/helpers";

interface UseBucketsTableColumnConfigProps {
  onEdit: (bucket: Bucket) => void;
  onDelete: (bucket: Bucket) => void;
}

const useBucketsTableColumnConfig = ({
  onEdit,
  onDelete,
}: UseBucketsTableColumnConfigProps): ColumnConfig<Bucket>[] => {
  const { t } = useTranslation("storageOverview");
  return useMemo(
    () => [
      {
        key: "name",
        dataIndex: "name",
        title: t("table.columns.name"),
        width: "18%",
      },
      {
        key: "size",
        dataIndex: "size",
        title: t("table.columns.size"),
        render: (value: number) => formatStorageSize(value),
        width: "15%",
      },
      {
        key: "objects",
        dataIndex: "objects",
        title: t("table.columns.objects"),
        width: "12%",
      },
      {
        key: "location",
        dataIndex: "location",
        title: t("table.columns.location"),
        width: "12%",
      },
      {
        key: "replicated",
        dataIndex: "replicated",
        title: t("table.columns.replicated"),
        render: (value: boolean) => (value ? t("common:Yes") : t("common:No")),
        width: "10%",
      },
      {
        key: "properties",
        dataIndex: "properties",
        title: t("table.columns.properties"),
        width: "18%",
        render: (value: string) => value || "",
      },
      {
        key: "created",
        dataIndex: "created",
        title: t("table.columns.created"),
        width: "14%",
      },
      {
        key: "actions",
        dataIndex: "",
        title: "",
        sortDisabled: true,
        width: "80px",
        render: (_value, record) => {
          const isEditDisabled = record.replicated || record.objectLock;
          const isDeleteDisabled = Boolean(record.objectLock);

          return (
            <div style={{ display: "flex", gap: 4 }}>
              <Tooltip title={isEditDisabled ? t("tooltips.editDisabled") : t("common:Edit")}>
                <span>
                  <Button type="tertiary" leftIcon="pen" disabled={isEditDisabled} onClick={() => onEdit(record)} />
                </span>
              </Tooltip>

              <Tooltip title={isDeleteDisabled ? t("tooltips.deleteDisabled") : t("common:Delete")}>
                <span>
                  <Button
                    type="tertiary"
                    leftIcon="trash-bin"
                    disabled={isDeleteDisabled}
                    onClick={() => onDelete(record)}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [onEdit, onDelete, t]
  );
};

export default useBucketsTableColumnConfig;
